/**
 * 一覧＋詳細型の右側詳細パネル(設計書 §5-3)。業務ロジックは持たない。
 *  - SplitView: 一覧(横幅いっぱい)と、開いたときだけ右に出るパネルを並べる
 *  - パネル幅 480px(狭い画面では min(480px, 100%))。スマホ(≤900px)は全画面
 *  - Esc と「閉じる」で閉じる。開いたら見出しへ、閉じたら元の場所へフォーカスを戻す
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconClose } from '@core/components/icons';

export function SplitView({ panel, children }: { panel: ReactNode | null; children: ReactNode }) {
  return (
    <div className={`split-view${panel ? ' has-panel' : ''}`}>
      <div className="split-main">{children}</div>
      {panel}
    </div>
  );
}

export default function DetailPanel({ title, subtitle, onClose, children, footer }: { title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    headingRef.current?.focus();
    return () => {
      returnTo.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // モーダル(dialog)が開いているときはそちらを先に閉じる
      if (document.querySelector('dialog[open]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // <aside> は使わない(layout.css の aside はサイドバーの指定で、色・幅・スマホの位置が付いてしまう)。
  // 名前付きの section は「領域(region)」として読み上げられる
  return (
    <section className="detail-panel" aria-labelledby={headingId}>
      <div className="detail-panel-head">
        <div className="detail-panel-titles">
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className="detail-panel-title">
            {title}
          </h2>
          {subtitle && <div className="detail-panel-subtitle">{subtitle}</div>}
        </div>
        <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={onClose} aria-label="詳細を閉じる">
          <IconClose size={16} />
        </button>
      </div>
      <div className="detail-panel-body">{children}</div>
      {footer && <div className="detail-panel-foot">{footer}</div>}
    </section>
  );
}

/** パネル内のまとまり(見出し＋中身) */
export function DetailSection({ title, children, actions }: { title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="detail-section">
      <div className="detail-section-head">
        <h3 className="detail-section-title">{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** 項目名と値の2列(dl) */
export function DetailList({ items }: { items: { label: ReactNode; value: ReactNode; numeric?: boolean }[] }) {
  return (
    <dl className="detail-list">
      {items.map((it, i) => (
        <div key={i} className="detail-list-row">
          <dt>{it.label}</dt>
          <dd className={it.numeric ? 'is-numeric' : undefined}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
